import { useCallback, useEffect, useState } from "react";
import type {
  Settings as SettingsType,
  CalendarSettings,
  ReinstallSettings,
  PhotosSettings,
  SheetSettings,
  DocuSignSettings,
  InvoiceSettings,
  SheetColumn,
  ConnectionStatus,
  Provider,
} from "@shared/types";
import { api, ApiError } from "../api/client.ts";

// ---------------------------------------------------------------------------
// Settings page (PRD §7.9 + §11). Every downstream config value is editable here
// and connections to Google / DocuSign are managed via the OAuth start flow.
// ---------------------------------------------------------------------------

const TEMPLATE_TOKENS = [
  "{customer}",
  "{address}",
  "{phone}",
  "{email}",
  "{notes}",
  "{amount}",
  "{removal_date}",
  "{removal_time}",
  "{reinstall_date}",
  "{today}",
  "{album_url}",
  "{type}",
];

type Toast = { kind: "success" | "error"; message: string };

/** Deep clone settings so edits never mutate the fetched response. */
function cloneSettings(s: SettingsType): SettingsType {
  return {
    calendar: { ...s.calendar },
    reinstall: { ...s.reinstall },
    photos: { ...s.photos },
    sheet: { ...s.sheet, columns: s.sheet.columns.map((c) => ({ ...c })) },
    docusign: { ...s.docusign, tabMap: { ...s.docusign.tabMap } },
    invoice: { ...s.invoice, placeholderMap: { ...s.invoice.placeholderMap } },
  };
}

/** Map/record editor rows are kept as an ordered array so empty/duplicate keys
 *  while typing don't collapse. Serialised back to Record<string,string> on save. */
type KvRow = { key: string; value: string };

function recordToRows(rec: Record<string, string>): KvRow[] {
  return Object.entries(rec).map(([key, value]) => ({ key, value }));
}

function rowsToRecord(rows: KvRow[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const { key, value } of rows) {
    const k = key.trim();
    if (k) out[k] = value;
  }
  return out;
}

export function Settings() {
  const [settings, setSettings] = useState<SettingsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);

  // Editable key→template rows kept alongside the typed settings state.
  const [tabRows, setTabRows] = useState<KvRow[]>([]);
  const [placeholderRows, setPlaceholderRows] = useState<KvRow[]>([]);

  // Connections.
  const [connections, setConnections] = useState<{
    google: ConnectionStatus;
    docusign: ConnectionStatus;
  } | null>(null);
  const [connError, setConnError] = useState<string | null>(null);
  const [connBusy, setConnBusy] = useState<Provider | null>(null);
  const [banner, setBanner] = useState<{ kind: "success" | "error"; message: string } | null>(
    null,
  );

  const loadSettings = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    api
      .getSettings()
      .then((s) => {
        const cloned = cloneSettings(s);
        setSettings(cloned);
        setTabRows(recordToRows(cloned.docusign.tabMap));
        setPlaceholderRows(recordToRows(cloned.invoice.placeholderMap));
      })
      .catch((e) =>
        setLoadError(e instanceof Error ? e.message : "Failed to load settings."),
      )
      .finally(() => setLoading(false));
  }, []);

  const loadConnections = useCallback(() => {
    setConnError(null);
    api
      .getConnections()
      .then(setConnections)
      .catch((e) =>
        setConnError(e instanceof Error ? e.message : "Failed to load connections."),
      );
  }, []);

  useEffect(() => {
    loadSettings();
    loadConnections();
  }, [loadSettings, loadConnections]);

  // Surface ?connected= / ?connect_error= banners from the OAuth redirect.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get("connected");
    const connectError = params.get("connect_error");
    if (connected) {
      setBanner({ kind: "success", message: `Connected ${connected} successfully.` });
    } else if (connectError) {
      setBanner({ kind: "error", message: `Connection failed: ${connectError}` });
    }
    if (connected || connectError) {
      params.delete("connected");
      params.delete("connect_error");
      const qs = params.toString();
      const url = window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
      window.history.replaceState({}, "", url);
    }
  }, []);

  // Auto-dismiss the save toast.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // ---- typed section patchers -------------------------------------------------
  const patchCalendar = (patch: Partial<CalendarSettings>) =>
    setSettings((s) => (s ? { ...s, calendar: { ...s.calendar, ...patch } } : s));
  const patchReinstall = (patch: Partial<ReinstallSettings>) =>
    setSettings((s) => (s ? { ...s, reinstall: { ...s.reinstall, ...patch } } : s));
  const patchPhotos = (patch: Partial<PhotosSettings>) =>
    setSettings((s) => (s ? { ...s, photos: { ...s.photos, ...patch } } : s));
  const patchSheet = (patch: Partial<SheetSettings>) =>
    setSettings((s) => (s ? { ...s, sheet: { ...s.sheet, ...patch } } : s));
  const patchDocuSign = (patch: Partial<DocuSignSettings>) =>
    setSettings((s) => (s ? { ...s, docusign: { ...s.docusign, ...patch } } : s));
  const patchInvoice = (patch: Partial<InvoiceSettings>) =>
    setSettings((s) => (s ? { ...s, invoice: { ...s.invoice, ...patch } } : s));

  // ---- sheet column editing ---------------------------------------------------
  const updateColumn = (i: number, patch: Partial<SheetColumn>) =>
    setSettings((s) => {
      if (!s) return s;
      const columns = s.sheet.columns.map((c, idx) => (idx === i ? { ...c, ...patch } : c));
      return { ...s, sheet: { ...s.sheet, columns } };
    });
  const addColumn = () =>
    setSettings((s) =>
      s
        ? { ...s, sheet: { ...s.sheet, columns: [...s.sheet.columns, { header: "", template: "" }] } }
        : s,
    );
  const removeColumn = (i: number) =>
    setSettings((s) =>
      s ? { ...s, sheet: { ...s.sheet, columns: s.sheet.columns.filter((_, idx) => idx !== i) } } : s,
    );

  // ---- save -------------------------------------------------------------------
  const handleSave = async () => {
    if (!settings) return;
    setToast(null);

    // Validate numeric fields before sending (inputs can hold transient values).
    const errors: string[] = [];
    if (!Number.isFinite(settings.calendar.durationMinutes) || settings.calendar.durationMinutes <= 0) {
      errors.push("Calendar duration must be a positive number of minutes.");
    }
    if (!Number.isFinite(settings.reinstall.offsetDays) || settings.reinstall.offsetDays < 0) {
      errors.push("Reinstall offset days must be zero or greater.");
    }
    if (
      settings.invoice.pricingDefault !== null &&
      (!Number.isFinite(settings.invoice.pricingDefault) || settings.invoice.pricingDefault < 0)
    ) {
      errors.push("Invoice default price must be a non-negative number or empty.");
    }
    if (errors.length) {
      setToast({ kind: "error", message: errors.join(" ") });
      return;
    }

    const payload: SettingsType = {
      ...settings,
      calendar: {
        ...settings.calendar,
        durationMinutes: Math.round(settings.calendar.durationMinutes),
      },
      reinstall: {
        ...settings.reinstall,
        offsetDays: Math.round(settings.reinstall.offsetDays),
      },
      sheet: {
        ...settings.sheet,
        columns: settings.sheet.columns.filter((c) => c.header.trim() || c.template.trim()),
      },
      docusign: { ...settings.docusign, tabMap: rowsToRecord(tabRows) },
      invoice: { ...settings.invoice, placeholderMap: rowsToRecord(placeholderRows) },
    };

    setSaving(true);
    try {
      const saved = await api.updateSettings(payload);
      const cloned = cloneSettings(saved);
      setSettings(cloned);
      setTabRows(recordToRows(cloned.docusign.tabMap));
      setPlaceholderRows(recordToRows(cloned.invoice.placeholderMap));
      setToast({ kind: "success", message: "Settings saved." });
    } catch (e) {
      const message =
        e instanceof ApiError ? e.message : e instanceof Error ? e.message : "Failed to save settings.";
      setToast({ kind: "error", message });
    } finally {
      setSaving(false);
    }
  };

  // ---- connection actions -----------------------------------------------------
  const handleConnect = async (provider: Provider) => {
    setConnError(null);
    setConnBusy(provider);
    try {
      const { url } = await api.getConnectUrl(provider);
      window.location.assign(url);
    } catch (e) {
      setConnError(e instanceof Error ? e.message : `Failed to start ${provider} connection.`);
      setConnBusy(null);
    }
  };

  const handleDisconnect = async (provider: Provider) => {
    setConnError(null);
    setConnBusy(provider);
    try {
      await api.disconnect(provider);
      loadConnections();
    } catch (e) {
      setConnError(e instanceof Error ? e.message : `Failed to disconnect ${provider}.`);
    } finally {
      setConnBusy(null);
    }
  };

  // ---- render -----------------------------------------------------------------
  if (loading) {
    return <div className="flex justify-center py-16 text-slate-400">Loading settings…</div>;
  }

  if (loadError || !settings) {
    return (
      <div className="mx-auto max-w-md">
        <div className="card p-6 text-center">
          <p className="font-semibold text-rose-700">Couldn’t load settings</p>
          <p className="mt-2 text-sm text-slate-600">{loadError ?? "Unknown error."}</p>
          <button onClick={loadSettings} className="btn-primary mt-4">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-28">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
          <p className="mt-1 text-sm text-slate-500">
            Configure every downstream artifact and manage integrations. Nothing here is hardcoded.
          </p>
        </div>
      </div>

      {banner && (
        <div
          className={`flex items-start justify-between gap-3 rounded-xl border p-4 text-sm ${
            banner.kind === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-rose-200 bg-rose-50 text-rose-800"
          }`}
        >
          <span>{banner.message}</span>
          <button
            onClick={() => setBanner(null)}
            className="shrink-0 font-medium underline-offset-2 hover:underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* ---------------------------------------------------------------- Connections */}
      <Section
        title="Connections"
        description="Authorize Google (Calendar, Sheets, Drive) and DocuSign. R&R jobs cannot run until both are healthy."
      >
        {connError && (
          <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{connError}</p>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <ConnectionCard
            label="Google"
            status={connections?.google}
            busy={connBusy === "google"}
            onConnect={() => handleConnect("google")}
            onDisconnect={() => handleDisconnect("google")}
          />
          <ConnectionCard
            label="DocuSign"
            status={connections?.docusign}
            busy={connBusy === "docusign"}
            onConnect={() => handleConnect("docusign")}
            onDisconnect={() => handleDisconnect("docusign")}
          />
        </div>
      </Section>

      {/* ---------------------------------------------------------------- Calendar */}
      <Section
        title="Calendar"
        description="Removal and reinstall events created on the connected Google Calendar."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Calendar ID">
            <input
              className="input"
              value={settings.calendar.calendarId}
              onChange={(e) => patchCalendar({ calendarId: e.target.value })}
              placeholder="primary"
            />
          </Field>
          <Field label="Timezone (IANA)">
            <input
              className="input"
              value={settings.calendar.timezone}
              onChange={(e) => patchCalendar({ timezone: e.target.value })}
              placeholder="America/Chicago"
            />
          </Field>
          <Field label="Removal title format">
            <input
              className="input"
              value={settings.calendar.removalTitleFormat}
              onChange={(e) => patchCalendar({ removalTitleFormat: e.target.value })}
            />
          </Field>
          <Field label="Reinstall title format">
            <input
              className="input"
              value={settings.calendar.reinstallTitleFormat}
              onChange={(e) => patchCalendar({ reinstallTitleFormat: e.target.value })}
            />
          </Field>
          <Field label="Default time (HH:mm)">
            <input
              type="time"
              className="input"
              value={settings.calendar.defaultTime}
              onChange={(e) => patchCalendar({ defaultTime: e.target.value })}
            />
          </Field>
          <Field label="Duration (minutes)">
            <input
              type="number"
              min={1}
              className="input"
              value={Number.isFinite(settings.calendar.durationMinutes) ? settings.calendar.durationMinutes : ""}
              onChange={(e) => patchCalendar({ durationMinutes: e.target.valueAsNumber })}
            />
          </Field>
        </div>
        <Field label="Notes template">
          <textarea
            className="input min-h-[140px] font-mono text-xs"
            value={settings.calendar.notesTemplate}
            onChange={(e) => patchCalendar({ notesTemplate: e.target.value })}
          />
        </Field>
        <TokenHint />
      </Section>

      {/* ---------------------------------------------------------------- Reinstall */}
      <Section
        title="Reinstall"
        description="How the reinstall date and event are derived from the removal."
      >
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Offset days">
            <input
              type="number"
              min={0}
              className="input"
              value={Number.isFinite(settings.reinstall.offsetDays) ? settings.reinstall.offsetDays : ""}
              onChange={(e) => patchReinstall({ offsetDays: e.target.valueAsNumber })}
            />
          </Field>
          <Checkbox
            label="Carry time of day from removal"
            checked={settings.reinstall.carryTime}
            onChange={(checked) => patchReinstall({ carryTime: checked })}
          />
          <Checkbox
            label="Include photo folder link in reinstall notes"
            checked={settings.reinstall.carryAlbumLink}
            onChange={(checked) => patchReinstall({ carryAlbumLink: checked })}
          />
        </div>
      </Section>

      {/* ---------------------------------------------------------------- Photos */}
      <Section title="Removal photos" description="Naming of the shared Google Drive folder created per job for removal photos.">
        <Field label="Folder name format">
          <input
            className="input"
            value={settings.photos.albumNameFormat}
            onChange={(e) => patchPhotos({ albumNameFormat: e.target.value })}
          />
        </Field>
        <TokenHint />
      </Section>

      {/* ---------------------------------------------------------------- Google Sheet */}
      <Section
        title="Google Sheet"
        description="The job-log spreadsheet and the column-by-column mapping written for each job."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Spreadsheet ID">
            <input
              className="input"
              value={settings.sheet.spreadsheetId}
              onChange={(e) => patchSheet({ spreadsheetId: e.target.value })}
              placeholder="1AbC…"
            />
          </Field>
          <Field label="Tab name">
            <input
              className="input"
              value={settings.sheet.tabName}
              onChange={(e) => patchSheet({ tabName: e.target.value })}
              placeholder="Jobs"
            />
          </Field>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="label mb-0">Columns</span>
            <button type="button" onClick={addColumn} className="btn-secondary">
              + Add column
            </button>
          </div>
          {settings.sheet.columns.length === 0 ? (
            <p className="rounded-lg border border-dashed border-slate-300 px-3 py-4 text-center text-sm text-slate-400">
              No columns yet. Add one to start mapping the sheet.
            </p>
          ) : (
            <div className="space-y-2">
              <div className="hidden grid-cols-[1fr_1fr_auto] gap-2 px-1 text-xs font-medium text-slate-500 sm:grid">
                <span>Header</span>
                <span>Template</span>
                <span className="sr-only">Remove</span>
              </div>
              {settings.sheet.columns.map((col, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                  <input
                    className="input"
                    value={col.header}
                    onChange={(e) => updateColumn(i, { header: e.target.value })}
                    placeholder="Header"
                    aria-label={`Column ${i + 1} header`}
                  />
                  <input
                    className="input font-mono text-xs"
                    value={col.template}
                    onChange={(e) => updateColumn(i, { template: e.target.value })}
                    placeholder="{customer}"
                    aria-label={`Column ${i + 1} template`}
                  />
                  <button
                    type="button"
                    onClick={() => removeColumn(i)}
                    className="btn-ghost text-rose-600 hover:bg-rose-50"
                    aria-label={`Remove column ${i + 1}`}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <TokenHint />
      </Section>

      {/* ---------------------------------------------------------------- DocuSign */}
      <Section
        title="DocuSign"
        description="R&R agreement envelope template and how its tabs are filled."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Template ID">
            <input
              className="input"
              value={settings.docusign.templateId}
              onChange={(e) => patchDocuSign({ templateId: e.target.value })}
            />
          </Field>
          <Field label="Signer role name">
            <input
              className="input"
              value={settings.docusign.signerRoleName}
              onChange={(e) => patchDocuSign({ signerRoleName: e.target.value })}
              placeholder="Customer"
            />
          </Field>
          <Field label="Send mode">
            <select
              className="input"
              value={settings.docusign.sendMode}
              onChange={(e) => patchDocuSign({ sendMode: e.target.value as DocuSignSettings["sendMode"] })}
            >
              <option value="sent">Send immediately</option>
              <option value="draft">Create draft for review</option>
            </select>
          </Field>
        </div>
        <KvEditor
          title="Tab map"
          keyLabel="Tab / field label"
          keyPlaceholder="customer_name"
          rows={tabRows}
          onChange={setTabRows}
        />
        <TokenHint />
      </Section>

      {/* ---------------------------------------------------------------- Invoice */}
      <Section title="Invoice" description="Invoice template, placeholder mapping, and delivery.">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Template file ID">
            <input
              className="input"
              value={settings.invoice.templateFileId}
              onChange={(e) => patchInvoice({ templateFileId: e.target.value })}
            />
          </Field>
          <Field label="Template type">
            <select
              className="input"
              value={settings.invoice.templateType}
              onChange={(e) =>
                patchInvoice({ templateType: e.target.value as InvoiceSettings["templateType"] })
              }
            >
              <option value="doc">Google Doc</option>
              <option value="sheet">Google Sheet</option>
            </select>
          </Field>
          <Field label="Default price (blank = use intake amount)">
            <input
              type="number"
              min={0}
              step="0.01"
              className="input"
              value={
                settings.invoice.pricingDefault === null ||
                settings.invoice.pricingDefault === undefined ||
                !Number.isFinite(settings.invoice.pricingDefault)
                  ? ""
                  : settings.invoice.pricingDefault
              }
              onChange={(e) =>
                patchInvoice({
                  pricingDefault: e.target.value === "" ? null : e.target.valueAsNumber,
                })
              }
              placeholder="—"
            />
          </Field>
          <Checkbox
            label="Email the finished invoice to the customer"
            checked={settings.invoice.deliverEmail}
            onChange={(checked) => patchInvoice({ deliverEmail: checked })}
          />
        </div>
        <KvEditor
          title="Placeholder map"
          keyLabel="Placeholder token"
          keyPlaceholder="{{CUSTOMER}}"
          rows={placeholderRows}
          onChange={setPlaceholderRows}
        />
        <TokenHint />
      </Section>

      {/* ---------------------------------------------------------------- Sticky save */}
      <div className="sticky bottom-0 -mx-4 border-t border-slate-200 bg-white/90 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-end gap-4">
          {toast && (
            <span
              className={`text-sm font-medium ${
                toast.kind === "success" ? "text-emerald-700" : "text-rose-700"
              }`}
            >
              {toast.message}
            </span>
          )}
          <button onClick={handleSave} disabled={saving} className="btn-primary">
            {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Presentational helpers
// ---------------------------------------------------------------------------

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="card p-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      {children}
    </label>
  );
}

function Checkbox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 self-end pb-2 text-sm font-medium text-slate-700">
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-slate-300 text-brand-500 focus:ring-brand-200"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}

function TokenHint() {
  return (
    <div className="rounded-lg bg-slate-50 p-3">
      <p className="mb-1 text-xs font-medium text-slate-500">Available template tokens</p>
      <div className="flex flex-wrap gap-1.5">
        {TEMPLATE_TOKENS.map((tok) => (
          <code
            key={tok}
            className="rounded bg-white px-1.5 py-0.5 text-xs text-slate-600 ring-1 ring-inset ring-slate-200"
          >
            {tok}
          </code>
        ))}
      </div>
    </div>
  );
}

function ConnectionCard({
  label,
  status,
  busy,
  onConnect,
  onDisconnect,
}: {
  label: string;
  status?: ConnectionStatus;
  busy: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
}) {
  const connected = status?.connected ?? false;
  const healthy = status?.healthy ?? false;
  const tone = !connected
    ? { dot: "bg-slate-300", text: "text-slate-500", word: "Not connected" }
    : healthy
      ? { dot: "bg-emerald-500", text: "text-emerald-700", word: "Connected" }
      : { dot: "bg-amber-500", text: "text-amber-700", word: "Needs attention" };

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between">
        <span className="font-semibold text-slate-900">{label}</span>
        <span className={`flex items-center gap-1.5 text-xs font-medium ${tone.text}`}>
          <span className={`h-2 w-2 rounded-full ${tone.dot}`} />
          {tone.word}
        </span>
      </div>
      {status?.detail && <p className="text-xs text-slate-500">{status.detail}</p>}
      {status?.expires_at && connected && (
        <p className="text-xs text-slate-400">
          Token expires {new Date(status.expires_at).toLocaleString()}
        </p>
      )}
      <div className="mt-auto flex gap-2">
        {connected ? (
          <>
            <button onClick={onConnect} disabled={busy} className="btn-secondary">
              {busy ? "…" : "Reconnect"}
            </button>
            <button
              onClick={onDisconnect}
              disabled={busy}
              className="btn-ghost text-rose-600 hover:bg-rose-50"
            >
              {busy ? "…" : "Disconnect"}
            </button>
          </>
        ) : (
          <button onClick={onConnect} disabled={busy} className="btn-primary">
            {busy ? "Connecting…" : "Connect"}
          </button>
        )}
      </div>
    </div>
  );
}

function KvEditor({
  title,
  keyLabel,
  keyPlaceholder,
  rows,
  onChange,
}: {
  title: string;
  keyLabel: string;
  keyPlaceholder: string;
  rows: KvRow[];
  onChange: (rows: KvRow[]) => void;
}) {
  const update = (i: number, patch: Partial<KvRow>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const add = () => onChange([...rows, { key: "", value: "" }]);
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="label mb-0">{title}</span>
        <button type="button" onClick={add} className="btn-secondary">
          + Add row
        </button>
      </div>
      {rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 px-3 py-4 text-center text-sm text-slate-400">
          No mappings yet.
        </p>
      ) : (
        <div className="space-y-2">
          <div className="hidden grid-cols-[1fr_1fr_auto] gap-2 px-1 text-xs font-medium text-slate-500 sm:grid">
            <span>{keyLabel}</span>
            <span>Template</span>
            <span className="sr-only">Remove</span>
          </div>
          {rows.map((row, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2">
              <input
                className="input font-mono text-xs"
                value={row.key}
                onChange={(e) => update(i, { key: e.target.value })}
                placeholder={keyPlaceholder}
                aria-label={`${keyLabel} ${i + 1}`}
              />
              <input
                className="input font-mono text-xs"
                value={row.value}
                onChange={(e) => update(i, { value: e.target.value })}
                placeholder="{customer}"
                aria-label={`Template ${i + 1}`}
              />
              <button
                type="button"
                onClick={() => remove(i)}
                className="btn-ghost text-rose-600 hover:bg-rose-50"
                aria-label={`Remove row ${i + 1}`}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
